import React, { useState, useEffect, useRef } from 'react';
import { TextField } from '@mui/material';
import { NumericFormat } from 'react-number-format';

export default function OptimizedTextField({
  value,
  onChange,
  debounceTime = 400, // Thời gian chờ mặc định: 400ms
  isNumeric = false,  // Cờ báo hiệu đây là trường nhập Số
  numericProps = {},  // Các cấu hình riêng cho NumericFormat
  ...rest             // Các props còn lại của TextField (label, error, size...)
}) {
  // 1. STATE NỘI BỘ: Giúp gõ phím mượt mà không chờ Form cha
  const [localValue, setLocalValue] = useState(value ?? '');
  const timerRef = useRef(null);

  // 2. ĐỒNG BỘ CHIỀU VÀO: Khi Form cha load dữ liệu từ API hoặc bấm Reset
  useEffect(() => {
    setLocalValue(value ?? '');
  }, [value]);

  // Dọn dẹp bộ đếm khi Component bị hủy
  useEffect(() => {
    return () => {
      if (timerRef.current) clearTimeout(timerRef.current);
    };
  }, []);

  // 3. HÀM BẮN DỮ LIỆU LÊN CHA
  const triggerChange = (newVal) => {
    // Ô SỐ: thư viện trả về CHUỖI ("5") trong khi form đã giữ SỐ (5) — xem formRules.coerceFieldValue.
    // So thẳng bằng `!==` thì lần nào cũng "khác nhau" và bắn onChange thừa; mỗi lần thừa là một lần
    // chạy lại `handle_value_changed`, tức thêm một lượt gọi API lấy giá cho cùng một con số.
    if (isNumeric && newVal !== '' && value !== '' && value !== null && value !== undefined
        && Number(newVal) === Number(value)) return;
    if (newVal !== value) {
      onChange(newVal);
    }
  };

  // 4. XỬ LÝ SỰ KIỆN GÕ PHÍM (TEXT THƯỜNG)
  const handleChange = (e) => {
    const newVal = e.target.value;
    setLocalValue(newVal); // Giao diện cập nhật tức thì

    // Khởi động lại bộ đếm
    if (timerRef.current) clearTimeout(timerRef.current);
    timerRef.current = setTimeout(() => {
      triggerChange(newVal);
    }, debounceTime);
  };

  // 5. XỬ LÝ SỰ KIỆN GÕ PHÍM (SỐ TIỀN/SỐ LƯỢNG)
  const handleNumericChange = (values) => {
    const newVal = values.value; 
    setLocalValue(newVal);

    if (timerRef.current) clearTimeout(timerRef.current);
    timerRef.current = setTimeout(() => {
      triggerChange(newVal);
    }, debounceTime);
  };

  // 6. XỬ LÝ SỰ KIỆN MẤT FOCUS (ON BLUR)
  // Đảm bảo dữ liệu được lưu ngay lập tức nếu user gõ xong và bấm nút "Lưu" liền
  const handleBlur = (e) => {
    if (timerRef.current) clearTimeout(timerRef.current);
    triggerChange(localValue);
    if (rest.onBlur) rest.onBlur(e);
  };

  // ================= RENDER =================
  if (isNumeric) {
    return (
      <NumericFormat
        customInput={TextField}
        value={localValue}
        onValueChange={handleNumericChange}
        onBlur={handleBlur}
        {...numericProps}
        {...rest}
      />
    );
  }

  return (
    <TextField
      value={localValue}
      onChange={handleChange}
      onBlur={handleBlur}
      {...rest}
    />
  );
}